import { Text, Dialog, Portal } from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@digital-services-gard-as/anchor-react";

type NewBankAccountDetectedModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    accountDisplay: string;
};

export const NewBankAccountDetectedModal = ({
    isOpen,
    onClose,
    onConfirm,
    accountDisplay
}: NewBankAccountDetectedModalProps) => {
    const { t } = useTranslation(["invoices"]);
    const cancelRef = useRef<HTMLButtonElement | null>(null);

    const handleConfirm = useCallback(() => {
        onClose();
        onConfirm();
    }, [onClose, onConfirm]);

    const handleOnOpenChange = useCallback(
        (e: { open: boolean }) => {
            if (!e.open) {
                onClose();
            }
        },
        [onClose]
    );

    const getInitialFocusEl = useCallback(() => cancelRef.current, [cancelRef]);
    return (
        <Dialog.Root
            open={isOpen}
            initialFocusEl={getInitialFocusEl}
            id="newBankAccountDetectedModal"
            role="alertdialog"
            onOpenChange={handleOnOpenChange}
        >
            <Portal>
                <Dialog.Backdrop>
                    <Dialog.Positioner>
                        <Dialog.Content data-testid="newBankAccountDetectedModal">
                            <Dialog.Header fontSize="lg" fontWeight="bold">
                                <Dialog.Title>{t("invoices:newBankDetectedHeader")}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                <Text data-testid="newBankAccountDetectedAccount">{accountDisplay}</Text>
                                <Text mt="1rem">{t("invoices:newBankDetectedMsg")}</Text>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button
                                    id="newBankDetectedCancelBtn"
                                    ref={cancelRef}
                                    onClick={onClose}
                                    variant="secondary"
                                >
                                    {t("invoices:closeBtn")}
                                </Button>
                                <Button id="newBankDetectedConfirmBtn" onClick={handleConfirm}>
                                    {t("invoices:createInvoice.bankDetails.addBankAccountBtn")}
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Dialog.Backdrop>
            </Portal>
        </Dialog.Root>
    );
};
